import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/api';

interface FormData {
  email: string;
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  color: '#cccccc',
  marginBottom: '8px',
  fontSize: '14px',
  fontWeight: 500,
};

const inputStyle = (invalid: boolean): React.CSSProperties => ({
  width: '100%',
  background: '#2a2a2a',
  border: `1px solid ${invalid ? '#f44336' : '#4a4a4a'}`,
  borderRadius: '4px',
  color: '#ffffff',
  padding: '10px 12px',
  fontSize: '14px',
  outline: 'none',
});

const ForgotPassword: React.FC = () => {
  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors },
  } = useForm<FormData>({ mode: 'onTouched' });
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setFocus('email');
  }, [setFocus]);

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      const result = await apiService.forgotPassword(data.email.trim().toLowerCase());
      setMessage(result.message);
      setSubmitted(true);
    } catch (err: any) {
      // The backend always returns 200 on this endpoint; any error here is a
      // network or server-level failure.
      setMessage('Something went wrong. Please try again in a moment.');
      setSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="app-layout min-h-screen flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center" style={{ marginBottom: '24px' }}>
          <div className="app-brand" style={{ fontSize: '32px', marginBottom: '16px' }}>
            babylon.markets
          </div>
          <h2 style={{ color: '#ffffff', fontSize: '24px', marginBottom: '8px' }}>
            Reset your password
          </h2>
          <p style={{ color: '#888888', fontSize: '14px' }}>
            Enter your email and we'll send you a reset link.
          </p>
        </div>
        <div className="app-card">
          {submitted ? (
            <div style={{ textAlign: 'center' }}>
              <p
                role="status"
                aria-live="polite"
                style={{ color: '#cccccc', fontSize: '14px', lineHeight: 1.6 }}
              >
                {message}
              </p>
              <p style={{ marginTop: '20px' }}>
                <Link to="/login" style={{ color: '#2962ff' }}>
                  Back to sign in
                </Link>
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
              <div>
                <label htmlFor="forgot-email" style={labelStyle}>
                  Email address
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@example.com"
                  aria-invalid={errors.email ? true : undefined}
                  aria-describedby={errors.email ? 'forgot-email-error' : undefined}
                  style={inputStyle(!!errors.email)}
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^\S+@\S+\.\S+$/,
                      message: 'Please enter a valid email address',
                    },
                  })}
                />
                {errors.email && (
                  <p
                    id="forgot-email-error"
                    style={{ marginTop: '6px', color: '#f44336', fontSize: '13px' }}
                  >
                    {errors.email.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary"
                style={{
                  width: '100%',
                  opacity: isLoading ? 0.5 : 1,
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {isLoading ? 'Sending...' : 'Send reset link'}
              </button>

              <p style={{ textAlign: 'center', fontSize: '13px' }}>
                <Link to="/login" style={{ color: '#2962ff' }}>
                  Back to sign in
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
